import { useState, type ReactNode } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { usePaesi } from '../../servizi/sessione.tsx'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Modale, Scelta } from '../../componenti/base.tsx'
import { erroriDellApi } from '../../componenti/errori-modulo.tsx'
import {
  GruppoCaselle, SceltaComune, SceltaMultipla, useLingue, vociDi,
} from '../../componenti/persona-campi.tsx'
import { codiceDaVoce, opzioniConAltreVoci } from '../../servizi/lingue.ts'
import { useRegole } from '../SchedaSezioni.tsx'
import { MASSIMO_LINGUE_VEICOLARI, comuneVuoto, type ComuneScelto } from '../../servizi/regole-anagrafica.ts'
import { giornoDiOggi } from '../../servizi/mediazioni.ts'

/**
 * «Completa i dati di …»: il mediatore, durante l'incontro, inserisce i dati della persona che
 * mancano (indicazioni CIDIS del 17/10/2026). Il percorso mostra solo i campi vuoti, una sezione
 * alla volta; quelli gia' scritti non si vedono e non si cambiano. Ogni sezione si salva da sola:
 * chi si ferma a meta' non perde quello che ha gia' inserito.
 *
 * I codici delle voci vengono dall'API (servizi/mediazioni.ts, `vociMancanti`).
 */

type Sezione = { codice: string; titolo: string; descrizione: string; voci: string[] }

const SEZIONI: Sezione[] = [
  {
    codice: 'chi', titolo: 'Chi è', descrizione: 'Genere, data e luogo di nascita, cittadinanza.',
    voci: ['genere', 'nascita', 'paese_nascita', 'comune_nascita', 'luogo_nascita_estero', 'cittadinanza'],
  },
  {
    codice: 'cf', titolo: 'Il codice fiscale', descrizione: 'Se non ce l’ha, scegli perché.',
    voci: ['codice_fiscale'],
  },
  {
    codice: 'contatti', titolo: 'Dove abita e come si contatta', descrizione: 'Residenza, telefono ed email.',
    voci: ['residenza_comune', 'residenza_indirizzo', 'telefono', 'email'],
  },
  {
    codice: 'lingue', titolo: 'Le lingue', descrizione: 'Le lingue che parla e quanto conosce l’italiano.',
    voci: ['lingua_madre', 'lingue_veicolari', 'livello_italiano'],
  },
  {
    codice: 'conoscenza', titolo: 'Come ha conosciuto il servizio', descrizione: 'Una o più risposte.',
    voci: ['canali_conoscenza'],
  },
]

/** Le sezioni con almeno un dato mancante, con le sole voci che mancano. */
export function sezioniDaCompletare(mancanti: string[]): Sezione[] {
  return SEZIONI.map((s) => ({ ...s, voci: s.voci.filter((v) => mancanti.includes(v)) }))
    .filter((s) => s.voci.length > 0)
}

type Valori = {
  genere_codice: string
  modo_nascita: 'data' | 'anno'
  data_nascita: string
  anno_nascita: string
  paese_nascita: string
  comune_nascita: ComuneScelto
  luogo_nascita_estero: string
  cittadinanza: string
  ha_cf: 'no' | 'si'
  cf: string
  motivo_assenza_cf_codice: string
  motivo_assenza_cf: string
  residenza_comune: ComuneScelto
  residenza_indirizzo: string
  telefono: string
  email: string
  lingua_madre: string
  lingue_veicolari: string[]
  livello_italiano: string
  canali_conoscenza: string[]
  canale_specifica: string
}

const VUOTI: Valori = {
  genere_codice: '', modo_nascita: 'data', data_nascita: '', anno_nascita: '', paese_nascita: '',
  comune_nascita: comuneVuoto(), luogo_nascita_estero: '', cittadinanza: '', ha_cf: 'no', cf: '',
  motivo_assenza_cf_codice: '', motivo_assenza_cf: '', residenza_comune: comuneVuoto(), residenza_indirizzo: '',
  telefono: '', email: '', lingua_madre: '', lingue_veicolari: [], livello_italiano: '', canali_conoscenza: [],
  canale_specifica: '',
}

const pieno = (v: string) => v.trim() !== ''

export function CompletaDati({ mediazioneId, nome, mancanti, chiudi, fatto }: {
  mediazioneId: string
  nome: string
  mancanti: string[]
  chiudi: () => void
  /** Dopo ogni salvataggio: la pagina rilegge la mediazione, il percorso resta com'era. */
  fatto: () => void
}) {
  // Le sezioni si fissano all'apertura: dopo ogni salvataggio la pagina si ricarica, e l'elenco
  // non deve cambiare sotto gli occhi di chi lo sta percorrendo.
  const [sezioni] = useState(() => sezioniDaCompletare(mancanti))
  const [indice, setIndice] = useState(0)
  const [salvate, setSalvate] = useState<string[]>([])
  const [v, setV] = useState<Valori>(VUOTI)
  const [errore, setErrore] = useState<string | null>(null)
  const [erroriCampi, setErroriCampi] = useState<Record<string, string>>({})
  const [inCorso, setInCorso] = useState(false)
  const { opzioni, ha, cataloghi } = useRegole()
  const { paesi } = usePaesi()
  const lingue = useLingue()

  const cambia = (parziale: Partial<Valori>) => setV((prima) => ({ ...prima, ...parziale }))
  const sezione = sezioni[indice]
  const manca = (voce: string) => !!sezione?.voci.includes(voce)
  const opzioniPaesi = paesi.map((p) => ({ valore: p.codice_iso3, etichetta: p.nome }))
  // Con le voci in piu' (553): «Persiano (farsi)» sotto la P, e si salva fas.
  const opzioniLingue = opzioniConAltreVoci(lingue)
  const oggi = giornoDiOggi()

  /** Il corpo della sezione: solo i campi mancanti che sono stati compilati. */
  function corpoEdErrori(): { corpo: Record<string, unknown>; errori: Record<string, string> } {
    const corpo: Record<string, unknown> = {}
    const errori: Record<string, string> = {}
    const comune = (campo: string, c: ComuneScelto) => {
      if (c.codice) corpo[campo] = c.codice
      else if (pieno(c.testo)) errori[campo] = 'Scegli il comune dall’elenco che compare mentre scrivi.'
    }
    if (manca('genere') && v.genere_codice) corpo.genere_codice = v.genere_codice
    if (manca('nascita')) {
      if (v.modo_nascita === 'data' && v.data_nascita) {
        if (v.data_nascita > oggi) errori.data_nascita = 'La data di nascita non può essere dopo oggi.'
        else corpo.data_nascita = v.data_nascita
      }
      if (v.modo_nascita === 'anno' && pieno(v.anno_nascita)) {
        const anno = Number(v.anno_nascita)
        if (!Number.isInteger(anno) || anno < 1900 || anno > Number(oggi.slice(0, 4))) {
          errori.anno_nascita = `Scrivi un anno fra 1900 e ${oggi.slice(0, 4)}.`
        } else corpo.anno_nascita = anno
      }
    }
    if (manca('paese_nascita') && v.paese_nascita) {
      corpo.paese_nascita = v.paese_nascita
      if (v.paese_nascita === 'ITA') comune('comune_nascita', v.comune_nascita)
      else if (pieno(v.luogo_nascita_estero)) corpo.luogo_nascita_estero = v.luogo_nascita_estero.trim()
    }
    if (manca('comune_nascita')) comune('comune_nascita', v.comune_nascita)
    if (manca('luogo_nascita_estero') && pieno(v.luogo_nascita_estero)) {
      corpo.luogo_nascita_estero = v.luogo_nascita_estero.trim()
    }
    if (manca('cittadinanza') && v.cittadinanza) corpo.cittadinanza = v.cittadinanza
    if (manca('codice_fiscale')) {
      if (v.ha_cf === 'si' && pieno(v.cf)) corpo.cf = v.cf.trim().toUpperCase()
      if (v.ha_cf === 'no' && v.motivo_assenza_cf_codice) {
        corpo.motivo_assenza_cf_codice = v.motivo_assenza_cf_codice
        if (ha('motivo_assenza_cf', v.motivo_assenza_cf_codice, 'richiede_specifica')) {
          if (pieno(v.motivo_assenza_cf)) corpo.motivo_assenza_cf = v.motivo_assenza_cf.trim()
          else errori.motivo_assenza_cf = 'Scrivi in attesa di che cosa.'
        }
      }
    }
    if (manca('residenza_comune')) comune('residenza_comune', v.residenza_comune)
    if (manca('residenza_indirizzo') && pieno(v.residenza_indirizzo)) corpo.residenza_indirizzo = v.residenza_indirizzo.trim()
    if (manca('telefono') && pieno(v.telefono)) corpo.telefono = v.telefono.trim()
    if (manca('email') && pieno(v.email)) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) errori.email = 'Questo indirizzo email non sembra giusto.'
      else corpo.email = v.email.trim()
    }
    if (manca('lingua_madre') && v.lingua_madre) corpo.lingua_madre = v.lingua_madre
    if (manca('lingue_veicolari') && v.lingue_veicolari.length) corpo.lingue_veicolari = v.lingue_veicolari
    if (manca('livello_italiano') && v.livello_italiano) corpo.livello_italiano = v.livello_italiano
    if (manca('canali_conoscenza') && v.canali_conoscenza.length) {
      corpo.canali_conoscenza = v.canali_conoscenza.map((codice) => ({
        codice,
        specifica: ha('canale_conoscenza', codice, 'richiede_specifica') && pieno(v.canale_specifica)
          ? v.canale_specifica.trim() : null,
      }))
    }
    return { corpo, errori }
  }

  const { corpo, errori: erroriLocali } = sezione ? corpoEdErrori() : { corpo: {}, errori: {} }
  const qualcosa = Object.keys(corpo).length > 0
  const errori = { ...erroriLocali, ...erroriCampi }

  function avanti() {
    setErrore(null); setErroriCampi({})
    setIndice((i) => i + 1)
  }

  async function salva() {
    if (Object.keys(erroriLocali).length) {
      setErrore('Controlla i campi segnati: poi salva, oppure salta la sezione.')
      return
    }
    setErrore(null); setErroriCampi({}); setInCorso(true)
    try {
      await api.patch(`/mediazioni/${mediazioneId}/persona`, corpo)
      setSalvate((s) => [...s, sezione.codice])
      fatto()
      setIndice((i) => i + 1)
    } catch (e) {
      setErroriCampi(erroriDellApi(e))
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  if (!sezione) {
    return (
      <Modale titolo={`I dati di ${nome}`} chiudi={chiudi}
              azioni={<Bottone variante="primario" onClick={chiudi}>Chiudi</Bottone>}>
        {salvate.length === 0
          ? <Avviso tipo="informazione">
              Non hai salvato niente: va bene così. I dati che mancano li ritrovi qui ogni volta che apri la mediazione.
            </Avviso>
          : <Avviso tipo="successo">
              Fatto, grazie: quello che hai inserito è nella scheda di {nome}. Se manca ancora qualcosa, lo ritrovi
              aprendo di nuovo la mediazione.
            </Avviso>}
      </Modale>
    )
  }

  return (
    <Modale larga titolo={`Completa i dati di ${nome}`} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={avanti}>Salta per ora</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={!qualcosa} onClick={() => void salva()}>
                {indice + 1 < sezioni.length ? 'Salva e vai avanti' : 'Salva'}
              </Bottone>
            </>}>
      <ol className="mediazioni-passi" aria-label="Sezioni da completare">
        {sezioni.map((s, i) => (
          <li key={s.codice} aria-current={i === indice ? 'step' : undefined}
              className={salvate.includes(s.codice) ? 'mediazioni-passo-fatto' : undefined}>
            {salvate.includes(s.codice) && <><span aria-hidden="true">✓ </span><span className="solo-lettore">Salvata: </span></>}
            {s.titolo}
          </li>
        ))}
      </ol>
      <h3 className="rete-titoletto">{sezione.titolo}</h3>
      <p className="mediazioni-introduzione">
        {sezione.descrizione} Qui ci sono solo i dati che mancano: se {nome} non li sa, lascia vuoto e vai avanti.
      </p>

      <div className="griglia-modulo">
        {manca('genere') && (
          <Scelta etichetta="Genere" valore={v.genere_codice} vuota="— scegli —" opzioni={opzioni('genere')}
                  cambia={(x) => cambia({ genere_codice: x })} errore={errori.genere_codice} />
        )}
        {manca('nascita') && (
          <>
            <Scelta etichetta="Della nascita sa" valore={v.modo_nascita}
                    opzioni={[{ valore: 'data', etichetta: 'il giorno preciso' }, { valore: 'anno', etichetta: 'solo l’anno' }]}
                    cambia={(x) => cambia({ modo_nascita: x as Valori['modo_nascita'] })} />
            {v.modo_nascita === 'data'
              ? <Campo tipo="date" etichetta="Data di nascita" valore={v.data_nascita} max={oggi}
                       cambia={(x) => cambia({ data_nascita: x })} errore={errori.data_nascita} />
              : <Campo tipo="number" etichetta="Anno di nascita" valore={v.anno_nascita} min={1900}
                       max={Number(oggi.slice(0, 4))} passi={1} modalitaTastiera="numeric"
                       cambia={(x) => cambia({ anno_nascita: x })} errore={errori.anno_nascita} />}
          </>
        )}
        {manca('paese_nascita') && (
          <>
            <Scelta etichetta="Paese d’origine" valore={v.paese_nascita} vuota="— scegli —" opzioni={opzioniPaesi}
                    cambia={(x) => cambia({ paese_nascita: x, comune_nascita: comuneVuoto(), luogo_nascita_estero: '' })}
                    errore={errori.paese_nascita} />
            {v.paese_nascita === 'ITA' && (
              <SceltaComune etichetta="Comune di nascita" valore={v.comune_nascita}
                            cambia={(x) => cambia({ comune_nascita: x })} errore={errori.comune_nascita} />
            )}
            {v.paese_nascita && v.paese_nascita !== 'ITA' && (
              <Campo etichetta="Città o località di nascita" valore={v.luogo_nascita_estero} maxLength={200}
                     cambia={(x) => cambia({ luogo_nascita_estero: x })} errore={errori.luogo_nascita_estero} />
            )}
          </>
        )}
        {manca('comune_nascita') && (
          <SceltaComune etichetta="Comune di nascita" valore={v.comune_nascita}
                        cambia={(x) => cambia({ comune_nascita: x })} errore={errori.comune_nascita} />
        )}
        {manca('luogo_nascita_estero') && (
          <Campo etichetta="Città o località di nascita" valore={v.luogo_nascita_estero} maxLength={200}
                 cambia={(x) => cambia({ luogo_nascita_estero: x })} errore={errori.luogo_nascita_estero} />
        )}
        {manca('cittadinanza') && (
          <Scelta etichetta="Cittadinanza" valore={v.cittadinanza} vuota="— scegli —" opzioni={opzioniPaesi}
                  cambia={(x) => cambia({ cittadinanza: x })} errore={errori.cittadinanza} />
        )}

        {manca('codice_fiscale') && (
          <>
            <Scelta etichetta="Ha il codice fiscale?" valore={v.ha_cf}
                    cambia={(x) => cambia({ ha_cf: x as Valori['ha_cf'] })}
                    opzioni={[{ valore: 'no', etichetta: 'No, non ancora' }, { valore: 'si', etichetta: 'Sì, lo inserisco' }]} />
            {v.ha_cf === 'si'
              ? <div className="campo-maiuscolo">
                  <Campo etichetta="Codice fiscale" valore={v.cf} maxLength={30} autoComplete="off"
                         cambia={(x) => cambia({ cf: x })} errore={errori.cf}
                         aiuto="Anche il codice numerico provvisorio di 11 cifre." />
                </div>
              : <Scelta etichetta="Perché non ce l’ha" valore={v.motivo_assenza_cf_codice} vuota="— scegli —"
                        opzioni={opzioni('motivo_assenza_cf')} errore={errori.motivo_assenza_cf_codice}
                        cambia={(x) => cambia({ motivo_assenza_cf_codice: x })} />}
            {v.ha_cf === 'no' && ha('motivo_assenza_cf', v.motivo_assenza_cf_codice, 'richiede_specifica') && (
              <Campo etichetta="In attesa di che cosa" valore={v.motivo_assenza_cf} maxLength={200}
                     cambia={(x) => cambia({ motivo_assenza_cf: x })} errore={errori.motivo_assenza_cf} />
            )}
          </>
        )}

        {manca('residenza_comune') && (
          <SceltaComune etichetta="Comune di residenza" valore={v.residenza_comune}
                        cambia={(x) => cambia({ residenza_comune: x })} errore={errori.residenza_comune} />
        )}
        {manca('residenza_indirizzo') && (
          <Campo etichetta="Indirizzo di residenza" valore={v.residenza_indirizzo} maxLength={300}
                 cambia={(x) => cambia({ residenza_indirizzo: x })} errore={errori.residenza_indirizzo}
                 aiuto="Via e numero civico." />
        )}
        {manca('telefono') && (
          <Campo tipo="tel" etichetta="Telefono" valore={v.telefono} maxLength={40} modalitaTastiera="tel"
                 cambia={(x) => cambia({ telefono: x })} errore={errori.telefono} />
        )}
        {manca('email') && (
          <Campo tipo="email" etichetta="Email" valore={v.email} modalitaTastiera="email"
                 cambia={(x) => cambia({ email: x })} errore={errori.email} />
        )}

        {manca('lingua_madre') && (
          <Scelta etichetta="Lingua madre" valore={v.lingua_madre} vuota="— scegli —" opzioni={opzioniLingue}
                  cambia={(x) => cambia({ lingua_madre: codiceDaVoce(x) })} errore={errori.lingua_madre} />
        )}
        {manca('livello_italiano') && (
          <Scelta etichetta="Quanto conosce l’italiano" valore={v.livello_italiano} vuota="— scegli —"
                  opzioni={opzioni('livello_italiano')} cambia={(x) => cambia({ livello_italiano: x })}
                  errore={errori.livello_italiano} />
        )}
      </div>

      {manca('lingue_veicolari') && (
        <SceltaMultipla etichetta="Altre lingue che parla" voci={lingue} scelte={v.lingue_veicolari}
                        vuota="— scegli una lingua —" massimo={MASSIMO_LINGUE_VEICOLARI}
                        errore={errori.lingue_veicolari} aiuto="Scegli una lingua e premi «Aggiungi»; puoi aggiungerne più d’una."
                        cambia={(x) => cambia({ lingue_veicolari: x })} />
      )}
      {manca('canali_conoscenza') && (
        <Canali valori={v} cambia={cambia} errore={errori.canali_conoscenza}
                voci={vociDi(cataloghi, 'canale_conoscenza')}
                conTesto={(codice) => ha('canale_conoscenza', codice, 'richiede_specifica')} />
      )}
      {/* Subito sopra i comandi, dove si e' appena premuto. */}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

function Canali({ valori: v, cambia, errore, voci, conTesto }: {
  valori: Valori
  cambia: (p: Partial<Valori>) => void
  errore?: string
  voci: { codice: string; nome: ReactNode }[]
  conTesto: (codice: string) => boolean
}) {
  const altro = v.canali_conoscenza.some(conTesto)
  return (
    <>
      <GruppoCaselle legenda="Come lo ha saputo" voci={voci} scelte={v.canali_conoscenza} errore={errore}
                     cambia={(codici) => cambia({ canali_conoscenza: codici, ...(codici.some(conTesto) ? {} : { canale_specifica: '' }) })} />
      {altro && (
        <div className="griglia-modulo">
          <Campo etichetta="Altro: quale" valore={v.canale_specifica} maxLength={300}
                 cambia={(x) => cambia({ canale_specifica: x })} aiuto="Facoltativo." />
        </div>
      )}
    </>
  )
}
